'use client'

import React from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import type { Discount } from '@/payload-types'
import { cn } from '@/utilities/cn'

type DiscountSummary = Pick<Discount, 'id' | 'name' | 'discountMode'>

type DiscountPillsProps = {
  discounts: DiscountSummary[]
}

const modeBadgeMap: Record<Discount['discountMode'], string> = {
  manual: 'Manual',
  coupon: 'Coupon',
  bulk: 'Bulk',
  automatic: 'Auto',
}

export function DiscountPills({ discounts }: DiscountPillsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useTranslations('shop')

  const activeDiscountId = searchParams.get('discount')

  const handleToggle = (discountId: number | null) => {
    const params = new URLSearchParams(searchParams.toString())
    if (discountId === null) {
      params.delete('discount')
    } else if (activeDiscountId === String(discountId)) {
      params.delete('discount')
    } else {
      params.set('discount', String(discountId))
    }
    const newParams = params.toString()
    router.push(pathname + (newParams ? `?${newParams}` : ''), { scroll: true })
  }

  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => handleToggle(null)}
        className={cn(
          'font-mono uppercase tracking-wider text-xs',
          'px-3 py-1.5 rounded-md transition-all duration-200',
          'flex items-center gap-2',
          'border',
          'min-h-[40px] md:min-h-[32px]',
          'max-w-full',
          'bg-black/30 border-white/8 hover:bg-white/10',
          'text-white/80 hover:text-neon-orange',
          !activeDiscountId && [
            'text-neon-orange',
            'border-[var(--neon-orange)]',
            'bg-[var(--neon-orange)]/10',
            'shadow-[0_0_15px_-3px_var(--neon-orange)]',
          ],
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-[var(--neon-orange)]/50 focus-visible:ring-offset-2',
          'hover:border-[var(--neon-orange)]/60 hover:shadow-[0_0_20px_-5px_var(--neon-orange)]'
        )}
        aria-pressed={!activeDiscountId}
        aria-label={t('allDiscounts')}
      >
        <span className="truncate">{t('allDiscounts')}</span>
      </button>

      {discounts.map((discount) => {
        const isActive = activeDiscountId === String(discount.id)
        const badge = modeBadgeMap[discount.discountMode]

        return (
          <button
            key={discount.id}
            onClick={() => handleToggle(discount.id)}
            className={cn(
              // Base styling matching CategoryPill
              'font-mono uppercase tracking-wider text-xs',
              'px-3 py-1.5 rounded-md transition-all duration-200',
              'flex items-center gap-2',
              'border',
              // Touch-friendly minimum size
              'min-h-[40px] md:min-h-[32px]',
              'max-w-full',
              // Default state - darker background, softer border
              'bg-black/30 border-white/8 hover:bg-white/10',
              'text-white/80 hover:text-neon-orange',
              // Active state - stronger glow
              isActive && [
                'text-neon-orange',
                'border-[var(--neon-orange)]',
                'bg-[var(--neon-orange)]/10',
                'shadow-[0_0_15px_-3px_var(--neon-orange)]',
              ],
              // Focus state for accessibility
              'focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-[var(--neon-orange)]/50 focus-visible:ring-offset-2',
              'hover:border-[var(--neon-orange)]/60 hover:shadow-[0_0_20px_-5px_var(--neon-orange)]'
            )}
            aria-pressed={isActive}
            aria-label={`Filter by discount ${discount.name}`}
          >
            <span className="truncate">{discount.name}</span>
            <span
              className={cn(
                'rounded-full bg-white/10 px-2 py-0.5 text-micro font-semibold uppercase text-white/70',
                isActive && 'bg-[var(--neon-orange)]/20 text-neon-orange',
              )}
            >
              {badge}
            </span>
          </button>
        )
      })}
    </div>
  )
}
